import React, { useEffect, useState } from 'react';

import { IoIosHeartDislike } from 'react-icons/io';

import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Stack, Tooltip } from '@mui/material';

import { createEdge, getEdges, removeEdge } from '../services/edgeService';
import Button from 'components/Button';
import { useToast } from 'hooks/useToast';

interface Props {
  loggedUserId: string;
  selectedUserId: string;
}

type EdgeType = 'FOLLOWED' | 'NETWORK';

interface Edge {
  id: number;
  from_user_uuid: string;
  to_user_uuid: string;
  type_code: EdgeType;
  value: any;
  from_node_type: string;
  to_node_type: string;
}

interface EdgeAction {
  type: string;
  icon: React.ReactElement;
  iconActive?: React.ReactElement;
  label: string;
  tooltip: string;
  variant: 'primary' | 'outline';
  show: boolean | undefined | number;
}

const CompanyActionsMenu: React.FC<Props> = ({ loggedUserId, selectedUserId }) => {
  const { toastError, toastSuccess, toastInfo } = useToast();
  const [edges, setEdges] = useState<Edge[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);

  const isOwnProfile = loggedUserId === selectedUserId;

  useEffect(() => {
    if (loggedUserId && selectedUserId) {
      getEdges(loggedUserId, selectedUserId).then(edges => {
        setEdges(edges);
        setIsFollowing(edges.some((edge: Edge) => edge.type_code === 'FOLLOWED'));
      });
    }
  }, [loggedUserId, selectedUserId]);

  const handleEdgeAction = async (type: EdgeType) => {
    if (isFollowing) {
      try {
        const existingEdge = edges.find(e => e.type_code === type);
        if (existingEdge) {
          await removeEdge(existingEdge.id);
          setIsFollowing(false);
          setEdges(prev => prev.filter(e => e.id !== existingEdge.id));
          toastSuccess('Deixou de seguir a empresa');
        }
      } catch (error) {
        console.error('Failed to remove edge:', error);
        toastError('Erro ao deixar de seguir');
      }
    } else {
      try {
        const response = await createEdge(
          loggedUserId,
          selectedUserId,
          type,
          undefined,
          'USER',
          'COMPANY'
        );
        if (response) {
          const newEdge = response as Edge;
          setEdges(prev => [...prev, newEdge]);
          setIsFollowing(true);
          toastSuccess('Agora você está seguindo a empresa');
        }
      } catch (error) {
        console.error('Failed to create edge:', error);
        toastError('Erro ao seguir a empresa');
      }
    }
  };

  const edgeActions: EdgeAction[] = [
    {
      type: 'FOLLOWED',
      icon: <FavoriteBorderIcon sx={{ color: isOwnProfile ? 'gray' : 'red' }} />,
      iconActive: <IoIosHeartDislike color={isOwnProfile ? 'gray' : 'red'} size={20} />,
      label: isFollowing ? 'Deixar de seguir' : 'Seguir',
      tooltip: isFollowing ? 'Deixar de seguir a empresa' : 'Seguir a empresa',
      variant: isFollowing ? 'outline' : 'primary',
      show: true,
    },
  ];

  const getActionLabel = (action: EdgeAction) => {
    return action.label;
  };

  const getActionIcon = (action: EdgeAction): React.ReactElement => {
    return isFollowing ? action.iconActive || action.icon : action.icon;
  };

  const getActionVariant = (action: EdgeAction) => {
    return action.variant;
  };

  const getActionTooltip = (action: EdgeAction) => {
    return action.tooltip;
  };

  const handleActionClick = (action: EdgeAction) => {
    if (isOwnProfile) {
      toastInfo('Você não pode realizar ações em seu próprio perfil.');
      return;
    }

    if (action.type === 'NETWORK') {
      // TODO:Implementar visualização da rede individual
      toastInfo('Funcionalidade em desenvolvimento');
    } else {
      handleEdgeAction(action.type as EdgeType);
    }
  };

  return (
    <Stack spacing={3} sx={{ minWidth: '200px' }}>
      {edgeActions
        .filter(action => action.show)
        .map(action => {
          const disabled = isOwnProfile;

          return (
            <Tooltip
              key={action.type}
              title={
                isOwnProfile
                  ? 'Você não pode realizar ações em seu próprio perfil'
                  : getActionTooltip(action)
              }
              arrow
              placement="left"
            >
              <span>
                <Button
                  size="medium"
                  variant={getActionVariant(action)}
                  icon={getActionIcon(action)}
                  onClick={() => handleActionClick(action)}
                  disabled={disabled}
                  fullWidth
                  style={{
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                  }}
                >
                  {getActionLabel(action)}
                </Button>
              </span>
            </Tooltip>
          );
        })}
    </Stack>
  );
};

export default CompanyActionsMenu;
